import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { motion } from "framer-motion";
import apiClient from "../api/client";
import moment from "moment";
import ExportModal from "../components/ExportModal";
import { saveAs } from "file-saver";

export default function MachineDetail() {
  const { id } = useParams();
  const [machine, setMachine] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observer = useRef();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const lastLogElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore]
  );

  const fetchMachineLogs = async (pageNum = 1, isLoadingMore = false) => {
    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await apiClient.get(
        `api/dashboard/machine/${id}/logs?page=${pageNum}&limit=20`
      );

      if (response.ok && response.data) {
        if (isLoadingMore) {
          setLogs((prevLogs) => [...prevLogs, ...response.data.logs]);
        } else {
          setMachine(response.data.machine);
          setLogs(response.data.logs);
        }
        setHasMore(response.data.pagination.hasMore);
      }
    } catch (error) {
      console.error("Error fetching machine logs:", error);
      setError("Errore nel caricamento dei log");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setLogs([]);
    fetchMachineLogs(1, false);
  }, [id, filter]);

  useEffect(() => {
    if (page > 1) {
      fetchMachineLogs(page, true);
    }
  }, [page]);

  const filteredLogs = logs.filter((log) => {
    if (filter === "all") return true;
    if (machine?.name === "LIYU") {
      switch (filter) {
        case "completed":
          return log.completion_percentage === 100;
        case "inProgress":
          return log.completion_percentage < 100;
        default:
          return true;
      }
    } else {
      return log.event_type.toLowerCase().includes(filter.toLowerCase());
    }
  });

  const getEventTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "alarm":
        return "bg-red-100 text-red-800";
      case "execution":
        return "bg-blue-100 text-blue-800";
      case "file_opened":
        return "bg-purple-100 text-purple-800";
      case "system":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const renderTableHeaders = () => {
    if (machine?.name === "LIYU") {
      return (
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Data/Ora Inizio
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Data/Ora Fine
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Nome File
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Passi Stampa
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Passi Totali
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Modalità
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Risoluzione
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Metri Quadri
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Completamento
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Prezzo/Litro
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Prezzo/m²
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Costo Totale
          </th>
        </tr>
      );
    } else {
      return (
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Data/Ora
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Tipo
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Stato
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Messaggio
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Tempo
          </th>
        </tr>
      );
    }
  };

  const renderTableRow = (log, index) => {
    const isLastElement = index === filteredLogs.length - 1;
    const rowProps = isLastElement ? { ref: lastLogElementRef } : {};

    if (machine?.name === "LIYU") {
      return (
        <motion.tr
          key={index}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
          {...rowProps}
        >
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {moment(log.start_datetime).format("DD/MM/YYYY HH:mm:ss")}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {log.end_datetime
              ? moment(log.end_datetime).format("DD/MM/YYYY HH:mm:ss")
              : "-"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.filename}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.print_steps || 0}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.total_steps || 0}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.print_mode || "-"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.resolution || "-"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.square_meters ? log.square_meters.toFixed(2) : "0.00"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <div className="flex items-center">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${log.completion_percentage || 0}%` }}
                ></div>
              </div>
              <span className="ml-2">
                {log.completion_percentage
                  ? log.completion_percentage.toFixed(1)
                  : "0.0"}
                %
              </span>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.price_per_liter
              ? `€${log.price_per_liter.toFixed(2)}`
              : "€0.00"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.price_per_square_meter
              ? `€${log.price_per_square_meter.toFixed(2)}`
              : "€0.00"}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.print_cost ? `€${log.print_cost.toFixed(2)}` : "€0.00"}
          </td>
        </motion.tr>
      );
    } else {
      return (
        <motion.tr
          key={index}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
          {...rowProps}
        >
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {moment(log.timestamp).format("DD/MM/YYYY HH:mm:ss")}
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span
              className={`px-2 py-1 text-xs rounded-full ${getEventTypeColor(
                log.event_type
              )}`}
            >
              {log.event_type}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.status}
          </td>
          <td className="px-6 py-4 text-sm text-gray-900">{log.message}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {log.execution_time || "-"}
          </td>
        </motion.tr>
      );
    }
  };

  const renderFilterButtons = () => {
    if (machine?.name === "LIYU") {
      return (
        <>
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg ${
              filter === "all"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Tutti
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`px-4 py-2 rounded-lg ${
              filter === "completed"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Completati
          </button>
          <button
            onClick={() => setFilter("inProgress")}
            className={`px-4 py-2 rounded-lg ${
              filter === "inProgress"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            In Corso
          </button>
        </>
      );
    } else {
      return (
        <>
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg ${
              filter === "all"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Tutti
          </button>
          <button
            onClick={() => setFilter("alarm")}
            className={`px-4 py-2 rounded-lg ${
              filter === "alarm"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Allarmi
          </button>
          <button
            onClick={() => setFilter("execution")}
            className={`px-4 py-2 rounded-lg ${
              filter === "execution"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Esecuzioni
          </button>
          <button
            onClick={() => setFilter("file_opened")}
            className={`px-4 py-2 rounded-lg ${
              filter === "file_opened"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            File
          </button>
        </>
      );
    }
  };

  const handleExport = async (dateRange) => {
    try {
      const response = await apiClient.get(
        `api/dashboard/machine/${machine.name}/export?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
        {},
        { responseType: "blob" }
      );

      if (response.ok) {
        const blob = new Blob([response.data], {
          type: "text/csv;charset=utf-8",
        });
        saveAs(
          blob,
          `${machine.name}_logs_${dateRange.startDate}_${dateRange.endDate}.csv`
        );
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setError("Errore durante l'esportazione dei dati");
    }
  };

  if (loading && !loadingMore) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="loading loading-spinner text-red-500"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-red-500 text-xl">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen p-6"
      >
        <div className=" mx-auto">
          {machine && (
            <>
              <div className="flex justify-between items-center mb-8 flex-wrap">
                <motion.h1
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-3xl font-bold text-gray-800"
                >
                  {machine.name}
                </motion.h1>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setIsExportModalOpen(true)}
                    className="btn btn-primary btn-sm"
                  >
                    Esporta CSV
                  </button>
                  <span className="text-sm text-gray-500">
                    Ultimo aggiornamento:{" "}
                    {moment(machine.lastUpdated).format("DD/MM/YYYY HH:mm:ss")}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {renderFilterButtons()}
                </div>
              </div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.05,
                    },
                  },
                }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">{renderTableHeaders()}</thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredLogs.map((log, index) =>
                        renderTableRow(log, index)
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {loadingMore && (
                <div className="flex justify-center my-4">
                  <div className="loading loading-spinner text-red-500"></div>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title={`Esporta log ${machine?.name}`}
      />
    </Layout>
  );
}
